import React, { PropsWithChildren, ReactNode } from 'react';
import { Box, IconButton, Stack, styled, SxProps, Theme, Typography } from '@mui/material';
import { COLOR_ACCENT, COLOR_STROKE, COLOR_WH } from 'shared';

import { Icons } from '..';

import { LinkBlock, LinkBlockProps } from './link-block';

interface ModalSidebarProps {
  onClose: VoidFunction;
  isOpen?: boolean;
  sx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  title?: ReactNode;
  icon?: ReactNode;
  transitionTime?: number;
  subtitle?: ReactNode;
  link?: LinkBlockProps;
}

const StyledWrapper = styled(Box)(({ theme }) => ({
  maxWidth: 427,
  backgroundColor: COLOR_WH,
  height: '100%',
  width: '100%',
  borderRadius: '20px',
  flexGrow: 1,
  marginLeft: 'auto',
  zIndex: 100,
  position: 'relative',
  paddingTop: 14,
  paddingBottom: 14,
  pointerEvents: 'auto',
}));

export const ModalSidebar = React.forwardRef<HTMLElement, PropsWithChildren<ModalSidebarProps>>(
  ({ children, sx, titleSx, title, icon, isOpen, transitionTime = 500, onClose, subtitle, link }, ref) => {
    return (
      <StyledWrapper tabIndex={-1} sx={sx} ref={ref}>
        <Box
          sx={{
            padding: '28px 32px',
            height: '100%',
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 32,
              top: 28,
              height: 48,
              width: 48,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: COLOR_STROKE,
              background: 'transparent',
              '&:hover': {
                background: 'transparent',
              },
            }}
          >
            <Icons.Close />
          </IconButton>
          {!!icon && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                borderRadius: '50%',
                border: '1px solid',
                borderColor: COLOR_ACCENT,
                background: COLOR_ACCENT,
                width: 48,
                height: 48,
                mb: 1,
              }}
            >
              {icon}
            </Stack>
          )}
          <Typography
            variant="h4"
            fontSize={{ xs: 24 }}
            fontWeight={600}
            className="black"
            sx={{
              maxWidth: 280,
              ...titleSx,
            }}
          >
            {title}
          </Typography>
          {!!subtitle && (
            <Typography mt={2} variant="h6" fontSize={{ xs: 20 }} className="black" fontWeight={500}>
              {subtitle}
            </Typography>
          )}
          <Typography mt={3} component="p" fontSize={{ xs: 16 }} className="black font-secondary" variant="body2">
            {children}
          </Typography>
          {!!link && <LinkBlock title={link.title} link={link.link} />}
        </Box>
      </StyledWrapper>
    );
  },
);
